import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { LoadingButton } from "@mui/lab";

function UploadFileDialog({
  open,
  onClose,
  onFileUpload,
  allowedFileTypes = [".docx"], // Default allowed file types
  dialogTitle = "Upload File", // Default dialog title
  uploadButtonText = "Upload File", // Default button text
  showJustification = false, // For pass-with-evidence checks
  justification = "",
  onJustificationChange = null,
  topContent = null, // Optional content rendered above the drop zone (e.g. policy fields)
  onBack = null, // Optional; when provided, shows a Back button (e.g. for multi-step flows)
}) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const validateAndSetFile = (selectedFile) => {
    const isValidFile = allowedFileTypes.some((type) =>
      selectedFile?.name.endsWith(type)
    );

    if (selectedFile && isValidFile) {
      setFile(selectedFile);
      setError(false);
    } else {
      setFile(null);
      setError(true);
    }
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = event.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const droppedFile = droppedFiles[0];
    validateAndSetFile(droppedFile);
  };

  // Full-screen drag overlay: show when dialog is open and user drags over the window
  useEffect(() => {
    if (!open) return;
    const onWindowDragEnter = (e) => {
      if (e.dataTransfer?.types?.includes("Files")) setIsDragOver(true);
    };
    document.body.addEventListener("dragenter", onWindowDragEnter);
    return () => document.body.removeEventListener("dragenter", onWindowDragEnter);
  }, [open]);

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    setIsLoading(true);
    if (!file) {
      setError(true);
      setIsLoading(false);
      return;
    }

    try {
      await onFileUpload(file); // Call the provided upload function
      setFile(null);
      setError(false);
      onClose();
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed!");
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    onClose();
    setFile(null);
    setError(false);
    setIsLoading(false);
  };

  return (
    <>
      {/* Full-screen drag-and-drop overlay when dialog is open */}
      {open && (
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            pointerEvents: isDragOver ? "auto" : "none",
            opacity: isDragOver ? 1 : 0,
            transition: "opacity 0.15s ease",
            backgroundColor: "rgba(3, 27, 137, 0.08)",
            border: "3px dashed #031B89",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 72, mb: 2, color: "#031B89" }} />
          <Typography variant="h5" sx={{ color: "#031B89", fontWeight: 600 }}>
            Drop file here
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Allowed: {allowedFileTypes.join(", ")}
          </Typography>
        </Box>
      )}

      <Dialog open={open} onClose={handleCancel} fullWidth>
        <DialogTitle fontWeight="bold" sx={{ alignItems: "center" }}>
          {dialogTitle}
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {topContent}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Select a file to upload. Allowed file types:{" "}
            {allowedFileTypes.join(", ")}.
          </Typography>

          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: "1px dashed",
              borderColor: error ? "#C71414" : isDragOver ? "#031B89" : "#031B89",
              backgroundColor: isDragOver ? "rgba(3, 27, 137, 0.04)" : "inherit",
              borderRadius: 2,
              py: 2,
              mb: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: "#031B89" }} />
            <Typography
              variant="body2"
              sx={{ color: error ? "#C71414" : "#031B89", fontWeight: 500 }}
            >
              Drag and drop a file here, or click to browse
            </Typography>
            <Typography
              variant="caption"
              sx={{ mt: 0.5, color: "text.secondary", textAlign: "center" }}
            >
              Allowed file types: {allowedFileTypes.join(", ")}
            </Typography>
            <input
              type="file"
              accept={allowedFileTypes.join(",")}
              hidden
              onChange={handleFileUpload}
              ref={fileInputRef}
            />
          </Box>

          {file && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <AttachFileIcon sx={{ color: "#031B89", mr: 1 }} />
              <Typography variant="body2" sx={{ color: "#031B89", mr: 1 }}>
                {file.name}
              </Typography>
              <IconButton onClick={handleRemoveFile} size="small">
                <DeleteIcon fontSize="small" sx={{ color: "#031B89" }} />
              </IconButton>
            </Box>
          )}

          {error && (
            <Typography variant="body2" color="#C71414" sx={{ mt: 1 }}>
              Please select a valid file to upload.
            </Typography>
          )}

          {showJustification && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={1}
                value={justification}
                onChange={(e) =>
                  onJustificationChange && onJustificationChange(e.target.value)
                }
                placeholder="Add justification (optional)..."
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", px: 2, gap: 1 }}>
          {onBack ? (
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none" }}>
              Back
            </Button>
          ) : (
            <Button onClick={handleCancel} variant="outlined" sx={{ textTransform: "none" }}>
              Cancel
            </Button>
          )}
          <LoadingButton
            onClick={handleUpload}
            variant="contained"
            disabled={!file || error}
            loading={isLoading}
            sx={{ textTransform: "none" }}
          >
            {uploadButtonText}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UploadFileDialog;
